import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { AnimatedNumber } from "../components/AnimatedNumber";
import { MapleLeaf, MapleWatermark } from "../components/MapleLeaf";
import { IMAGES } from "../data/images";
import { GRADE_ROUTES, INTAKES } from "../data/programmes";
import { EASE } from "../utils/motion";
const STEPS = [{
  title: "Discover MCC",
  copy: "Explore the campus, the OSSD pathway and the student experience. A visit is the best place to start.",
  link: {
    label: "About MCC",
    to: "/about"
  }
}, {
  title: "Understand the programme",
  copy: "The University Pathway Program runs over two semesters, with 6–8 courses and an Individual Growth Plan.",
  link: {
    label: "Curriculum",
    to: "/academics/curriculum"
  }
}, {
  title: "Check requirements",
  copy: "Applications are assessed on transcripts, the MCC entrance examination and Grade 7–12 performance.",
  link: {
    label: "Entry requirements",
    to: "/admissions/requirements"
  }
}, {
  title: "Speak to Admissions",
  copy: "Choose the September or January intake, and confirm fees and timelines directly with the MCC team before applying.",
  link: {
    label: "Contact MCC",
    to: "/contact"
  }
}, {
  title: "Apply",
  copy: "Complete the application form and upload supporting documents. A non-refundable application fee applies; confirm the current amount.",
  link: {
    label: "Start application",
    to: "/admissions/apply"
  }
}, {
  title: "Placement test & next steps",
  copy: "Candidates sit a Mathematics and English placement test, online or at the college. Admissions then follows up directly.",
  link: null
}];
function Step({
  index,
  onActive,
  children
}: {
  index: number;
  onActive: (i: number) => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    margin: "-45% 0px -45% 0px"
  });
  useEffect(() => {
    if (inView) onActive(index);
  }, [inView, index, onActive]);
  return <div ref={ref} className="flex min-h-[55vh] flex-col justify-center border-b border-[#dedbd3] py-12 last:border-b-0" data-id="element-535" data-mp="jpqm88ann7hv">{children}</div>;
}
export function Admissions() {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  return <>
      <PageHero variant="split" eyebrow="ADMISSIONS" title="A clear route to MCC." description="Two intakes a year, September and January. Six steps from first question to first day." image={IMAGES.campus} imageAlt="Students walking through the MCC campus" breadcrumb="Admissions" data-id="element-536" data-mp="4t9gbwl8il2p" />

      <main data-id="element-537" data-mp="3s0bsb165gc4t">
        <section className="mx-auto grid max-w-[1440px] gap-10 px-5 py-16 md:px-9 lg:grid-cols-12 lg:py-24" data-id="element-538" data-mp="1v5p513vmx7wa">
          <div className="hidden lg:col-span-5 lg:block" data-id="element-539" data-mp="1q62p2mof9djs">
            <div className="sticky top-28 flex h-[calc(100vh-8rem)] flex-col justify-between py-10" data-id="element-540" data-mp="1q4j80tuxyygy">
              <div data-id="element-541" data-mp="1xo0wmln1mekd">
                <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-542" data-mp="22p81ocxswznc">THE ADMISSIONS JOURNEY</p>
                <div className="relative mt-6 h-[13rem] overflow-hidden" data-id="element-543" data-mp="27g6ireqm61rn">
                  <AnimatePresence mode="popLayout" initial={false} data-id="element-544" data-mp="27iap03bzjcv3">
                    <motion.span key={active} initial={{
                    y: reduce ? 0 : "100%",
                    opacity: 0
                  }} animate={{
                    y: "0%",
                    opacity: 1
                  }} exit={{
                    y: reduce ? 0 : "-100%",
                    opacity: 0
                  }} transition={{
                    duration: reduce ? 0.01 : 0.6,
                    ease: EASE
                  }} className="absolute left-0 top-0 block text-[13rem] font-medium leading-none tracking-[-0.09em] text-[#061f38]" data-id="element-545" data-mp="2cehww1nxyyfo">
                      0{active + 1}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
              <ol className="space-y-3" data-id="element-546" data-mp="303u4wy7j90ge">
                {STEPS.map((s, i) => <li key={s.title} className={`flex items-center gap-4 text-sm transition-colors duration-300 ${i === active ? "text-[#061f38]" : "text-[#9aa1a6]"}`} data-id="element-547" data-mp="3455vp7257rq6" data-mpi={i}>
                    <span className={`h-px transition-[width,background-color] duration-300 ${i === active ? "w-10 bg-[#bb1e35]" : "w-4 bg-[#cfcac0]"}`} data-id="element-548" data-mp="2xq6zk3zjrgsg" />
                    {s.title}
                  </li>)}
              </ol>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7" data-id="element-549" data-mp="37qhfhwcyv2bb">
            {STEPS.map((s, i) => <Step key={s.title} index={i} onActive={setActive} data-id="element-550" data-mp="3llt89y7kaipl" data-mpi={i}>
                <Reveal data-id="element-551" data-mp="3go1jve64leq5">
                  <p className="text-sm font-semibold text-[#bb1e35] lg:hidden" data-id="element-552" data-mp="3gmqfbc4uw2xz">0{i + 1}</p>
                  <h2 className="mt-2 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-6xl" data-id="element-553" data-mp="3vcxm2uli3xiu">{s.title}</h2>
                  <p className="mt-6 max-w-md text-lg leading-8 text-[#3d4b57]" data-id="element-554" data-mp="3qgyu6x0yrxdo">{s.copy}</p>
                  {s.link && <Link to={s.link.to} className="group mt-8 inline-flex items-center gap-2 border-b border-[#061f38] pb-1 text-sm font-semibold transition-colors duration-200 hover:border-[#bb1e35] hover:text-[#bb1e35]" data-id="element-555" data-mp="47xwzlk3batp">
                      {s.link.label} <ArrowRightIcon size={15} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-556" data-mp="ru9lf9pvx1c9" />
                    </Link>}
                </Reveal>
              </Step>)}
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#f2f0eb] py-24 md:py-32" data-id="element-557" data-mp="44p0wme31w0v">
          <MapleWatermark className="-right-24 -top-16 h-[30rem] w-[30rem]" tone="text-[#061f38]/[0.04]" data-id="element-558" data-mp="8vxb3kn8llf3" />
          <div className="relative mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-559" data-mp="3ra6l2942ii8t">
            <Reveal data-id="element-560" data-mp="3r7tvb9s2j3l7">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-561" data-mp="3vb8kapxziuas">INTAKES</p>
              <h2 className="mt-5 max-w-2xl text-4xl font-medium tracking-[-0.06em] md:text-6xl" data-id="element-562" data-mp="3hbquc78ydazu">Two ways in: September or January.</h2>
            </Reveal>
            <div className="mt-14 grid gap-px bg-[#cfcac0] md:grid-cols-2" data-id="element-563" data-mp="3maafw6jmwkro">
              {INTAKES.map((intake, i) => <Reveal key={intake.month} delay={i * 0.1} className="bg-[#f2f0eb] p-8 md:p-10" data-id="element-564" data-mp="1rwpqfe0pk16e" data-mpi={i}>
                  <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.16em] text-[#bb1e35]" data-id="element-565" data-mp="1n1se9y84tbpa"><MapleLeaf size={11} data-id="element-566" data-mp="1j06zg7c1encg" /> {intake.name.toUpperCase()}</p>
                  <h3 className="mt-6 text-[clamp(3rem,6vw,5.5rem)] font-medium leading-none tracking-[-0.07em]" data-id="element-567" data-mp="1e4rr1uwstjnf">{intake.month}</h3>
                  <p className="mt-6 max-w-sm text-base leading-7 text-[#3d4b57]" data-id="element-568" data-mp="2ar4tbeq54dzi">{intake.summary}</p>
                  <p className="mt-3 max-w-sm text-sm leading-6 text-[#6b7378]" data-id="element-569" data-mp="26q1b4lkme5v4">{intake.completes}</p>
                </Reveal>)}
            </div>

            <div className="mt-20 grid gap-12 lg:grid-cols-12" data-id="element-570" data-mp="1wt6hafzaktn6">
              <Reveal className="lg:col-span-4" data-id="element-571" data-mp="qe5lcaa9obgl">
                <h3 className="text-3xl font-medium tracking-[-0.05em]" data-id="element-572" data-mp="qexgxl3v696v">How long will it take?</h3>
                <p className="mt-4 max-w-xs text-sm leading-6 text-[#4f5963]" data-id="element-573" data-mp="ld82rwlchfek">Programme length depends on the grade your child enters.</p>
              </Reveal>
              <div className="lg:col-span-8" data-id="element-574" data-mp="g8w30s4v2r0g">
                <table className="w-full border-collapse text-left" data-id="element-575" data-mp="g7l538y3kdh9">
                  <thead data-id="element-576" data-mp="l80g3tuwp8w2">
                    <tr className="border-b-2 border-[#061f38] text-xs font-semibold tracking-[0.14em] text-[#6b7378]" data-id="element-577" data-mp="l6pcpv5di35h">
                      <th scope="col" className="py-3" data-id="element-578" data-mp="u13zsjw5ahz5">ENTRY GRADE</th>
                      <th scope="col" className="py-3" data-id="element-579" data-mp="u0u54j05nmn4">DURATION</th>
                      <th scope="col" className="py-3" data-id="element-580" data-mp="yz5fcuud3z5v">TYPICAL AGE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-581" data-mp="13ztb1ttjd46f">
                    {GRADE_ROUTES.map(g => <tr key={g.grade} className="border-b border-[#cfcac0]" data-id="element-582" data-mp="18xbjzk7jzjef">
                        <th scope="row" className="py-5 text-lg font-medium tracking-[-0.02em]" data-id="element-583" data-mp="35r68xyx36bsz">{g.grade}</th>
                        <td className="py-5 text-lg tabular-nums" data-id="element-584" data-mp="3aoy4r40uqi2g"><AnimatedNumber value={g.duration} data-id="element-585" data-mp="3artzyklbesn7" /> months</td>
                        <td className="py-5 text-base tabular-nums text-[#4f5963]" data-id="element-586" data-mp="1ggkrf7wi180u">{g.ages}</td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="mt-8 flex flex-wrap items-center justify-between gap-6" data-id="element-587" data-mp="1q6njn5vsqhy4">
                  <p className="text-xs text-[#6b7378]" data-id="element-588" data-mp="1l8ccj2wvyljl">From MCC’s published admissions information. Confirm current dates with Admissions.</p>
                  <Link to="/admissions/fast-track" className="group inline-flex items-center gap-2 bg-[#c5233d] px-5 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#a71931]" data-id="element-589" data-mp="xl45affwd2ex">
                    <MapleLeaf size={13} data-id="element-590" data-mp="16hd4pu3vk112" /> Need it faster? Fast Track <ArrowRightIcon size={15} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-591" data-mp="12h1kujjc64qn" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <CTABand title="Ready when you are. Questions welcome before then." image={IMAGES.campus} data-id="element-592" data-mp="2dzm6x2k6byhh" />
    </>;
}